import React, { useState, useCallback, useMemo, useRef } from "react";
import { updateOrderStatus } from "../../services/order-service.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import Button from "../comman/button.js";
import Modal from "../comman/modal.js";
import Textarea from "../comman/textarea.js";

const OrderModal = ({ closeModal, orderId }) => {
    const [showTextarea, setShowTextarea] = useState(false);
    const [otherReason, setOtherReason] = useState("");
    const [selectedReasonKey, setSelectedReasonKey] = useState(null); // Store reason key instead of translated text
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { language } = useLanguage();
    const hasSubmittedRef = useRef(false); // Additional guard against multiple submissions

    // Memoize translations to prevent recalculation on every render
    const translations = useMemo(() => ({
        selectReason: getTranslation('orderModal.selectReason', language),
        preparationTimeIssue: getTranslation('orderModal.preparationTimeIssue', language),
        ingredientsOutOfStock: getTranslation('orderModal.ingredientsOutOfStock', language),
        deliveryIssue: getTranslation('orderModal.deliveryIssue', language),
        other: getTranslation('orderModal.other', language),
        enterReasonHere: getTranslation('orderModal.enterReasonHere', language),
        submit: getTranslation('orderModal.submit', language)
    }), [language]);

    // Get the actual reason text based on selected key
    const getReasonText = useCallback(() => {
        if (selectedReasonKey === 'other') {
            return otherReason;
        }
        if (selectedReasonKey && translations[selectedReasonKey]) {
            return translations[selectedReasonKey];
        }
        return "";
    }, [selectedReasonKey, otherReason, translations]);

    const handleButtonClick = useCallback((reasonKey) => {
        if (isSubmitting) return; // Prevent clicks during submission
        
        setSelectedReasonKey(reasonKey);
        if (reasonKey === 'other') {
            setShowTextarea(true);
            setOtherReason("");
        } else {
            setShowTextarea(false);
            setOtherReason("");
        }
    }, [isSubmitting]);

    const handleTextareaChange = useCallback((e) => {
        if (isSubmitting) return; // Prevent changes during submission
        setOtherReason(e.target.value);
    }, [isSubmitting]);

    const handleSubmit = useCallback(async () => {
        // CRITICAL: Prevent multiple submissions
        if (isSubmitting || hasSubmittedRef.current || !orderId) {
            return;
        }

        const reasonText = getReasonText();
        
        // Validate that a reason is selected and has valid text
        if (!selectedReasonKey || !reasonText || !reasonText.trim()) {
            return;
        }

        hasSubmittedRef.current = true;
        setIsSubmitting(true);

        try {
            await updateOrderStatus(orderId, "DECLINED", reasonText.trim());
            closeModal();
        } catch (error) {
            // Reset submission state on error so user can retry
            hasSubmittedRef.current = false;
            setIsSubmitting(false);
            // Error handling is done by the service layer
        }
    }, [isSubmitting, selectedReasonKey, getReasonText, orderId, closeModal]);

    // Prevent modal close during submission
    const handleClose = useCallback(() => {
        if (isSubmitting) return;
        closeModal();
    }, [isSubmitting, closeModal]);

    return (
        <Modal
            isOpen={true}
            onClose={handleClose}
            title={translations.selectReason}
            size="md"
        >
            <div className="mt-4 space-y-3">
                <button
                    type="button"
                    onClick={() => handleButtonClick('preparationTimeIssue')}
                    disabled={isSubmitting}
                    className={`w-full flex items-center justify-start px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        selectedReasonKey === 'preparationTimeIssue'
                            ? 'bg-gray-900 dark:bg-[#21262d] text-white dark:text-[#c9d1d9] border-2 border-gray-900 dark:border-gray-400 shadow-md'
                            : 'bg-transparent text-gray-700 dark:text-[#c9d1d9] border border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#1c2128]'
                    } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    🕒 {translations.preparationTimeIssue}
                </button>
                <button
                    type="button"
                    onClick={() => handleButtonClick('ingredientsOutOfStock')}
                    disabled={isSubmitting}
                    className={`w-full flex items-center justify-start px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        selectedReasonKey === 'ingredientsOutOfStock'
                            ? 'bg-gray-900 dark:bg-[#21262d] text-white dark:text-[#c9d1d9] border-2 border-gray-900 dark:border-gray-400 shadow-md'
                            : 'bg-transparent text-gray-700 dark:text-[#c9d1d9] border border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#1c2128]'
                    } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    🍽 {translations.ingredientsOutOfStock}
                </button>
                <button
                    type="button"
                    onClick={() => handleButtonClick('deliveryIssue')}
                    disabled={isSubmitting}
                    className={`w-full flex items-center justify-start px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        selectedReasonKey === 'deliveryIssue'
                            ? 'bg-gray-900 dark:bg-[#21262d] text-white dark:text-[#c9d1d9] border-2 border-gray-900 dark:border-gray-400 shadow-md'
                            : 'bg-transparent text-gray-700 dark:text-[#c9d1d9] border border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#1c2128]'
                    } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    🚚 {translations.deliveryIssue}
                </button>
                <button
                    type="button"
                    onClick={() => handleButtonClick('other')}
                    disabled={isSubmitting}
                    className={`w-full flex items-center justify-start px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        selectedReasonKey === 'other'
                            ? 'bg-gray-900 dark:bg-[#21262d] text-white dark:text-[#c9d1d9] border-2 border-gray-900 dark:border-gray-400 shadow-md'
                            : 'bg-transparent text-gray-700 dark:text-[#c9d1d9] border border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#1c2128]'
                    } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    ❌ {translations.other}
                </button>
                {showTextarea && (
                    <Textarea
                        value={otherReason}
                        onChange={handleTextareaChange}
                        name="otherReason"
                        placeholder={translations.enterReasonHere}
                        rows={4}
                        disabled={isSubmitting}
                    />
                )}
            </div>
            <div className="mt-6 text-right">
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !selectedReasonKey || (selectedReasonKey === 'other' && !otherReason.trim())}
                    loading={isSubmitting}
                >
                    {translations.submit}
                </Button>
            </div>
        </Modal>
    );
};
export default OrderModal;
